import { Button } from '@ctw/shared/components/button';
import { TelemetryBoundary } from '@ctw/shared/context/telemetry/telemetry-boundary';
import type { TelemetryUserInteraction } from '@ctw/shared/context/telemetry/user-interactions';
import { type ClassName, tw, twx } from '@ctw/shared/utils/tailwind';
import type { PropsWithChildren } from 'react';

export interface SimplePaginationProps {
  currentPage: number;
  setCurrentPage: (n: number) => void;
  hasNext?: boolean;
}

export const SimplePagination = ({
  setCurrentPage,
  currentPage,
  hasNext,
}: SimplePaginationProps) => {
  if (hasNext || currentPage > 1) {
    return (
      <TelemetryBoundary
        boundaryName="PaginationNextPrevious"
        initialContext={{
          paginationCurrentPage: currentPage,
          paginationHasNext: hasNext,
        }}
      >
        {() => (
          <div className={tw`simple-pagination flex justify-end space-x-3 px-6 py-3`}>
            <PaginationButton
              className={twx({ invisible: currentPage === 1 })}
              telemetryInteractionName="pagination_previous"
              telemetryContext={{
                paginatedFromPage: currentPage,
                paginatedToPage: currentPage - 1,
              }}
              onClick={() => setCurrentPage(currentPage - 1)}
            >
              Prev
            </PaginationButton>

            <PaginationButton
              className={twx({ invisible: !hasNext })}
              telemetryInteractionName="pagination_next"
              telemetryContext={{
                paginatedFromPage: currentPage,
                paginatedToPage: currentPage + 1,
              }}
              onClick={() => setCurrentPage(currentPage + 1)}
            >
              Next
            </PaginationButton>
          </div>
        )}
      </TelemetryBoundary>
    );
  }
  return <></>;
};

interface PaginationButtonProps extends PropsWithChildren {
  className?: ClassName;
  onClick: () => void;
  telemetryInteractionName: TelemetryUserInteraction;
  telemetryContext: Record<string, unknown>;
}

const PaginationButton = ({
  children,
  className,
  onClick,
  telemetryInteractionName,
  telemetryContext,
}: PaginationButtonProps) => (
  <Button
    type="button"
    variant="secondary"
    className={twx(className)}
    onClick={onClick}
    telemetryInteractionName={telemetryInteractionName}
    telemetryContext={telemetryContext}
  >
    {children}
  </Button>
);
